// Investor module API endpoints.
//
// Endpoints take camelCase view-model inputs and return camelCase view-model
// outputs. Snake_case lives ONLY between the network and the parser:
// outbound, this file lowers camelCase Inputs into PayloadDto shapes before
// sending; inbound, the parser converts response DTOs to view models.
// Downstream code (containers, components, composables) never sees a
// snake_case field.

import { adminApi as api } from '@core/api/httpClient'
import type {
  ApproveInvestorOnboardingPayloadDto,
  RejectInvestorOnboardingPayloadDto,
  ReviewBankAccountPayloadDto,
  UpdateDocumentPayloadDto,
  UpdateInvestorStatusPayloadDto,
  ApproveAmlScreeningPayloadDto,
  ApprovePepScreeningPayloadDto,
  RejectKycPayloadDto,
  InvestorStatusCountsDto,
} from './investorContracts'
import type {
  BankAccount,
  BankAccountListResponse,
  ComplianceKycDetails,
  GetBankAccountsParams,
  GetInvestorListParams,
  InvestorDetails,
  InvestorListResponse,
  ComplianceKycListResponse,
  ReviewBankAccountInput,
  UpdateDocumentInput,
  UpdateInvestorStatusInput,
  ApproveAmlScreeningInput,
  ApproveInvestorOnboardingInput,
  ApprovePepScreeningInput,
  RejectInvestorOnboardingInput,
  InvestorRiskProfile,
  InvestorSourceOfFunds,
} from '../types/investorTypes'
import {
  parseBankAccount,
  parseBankAccountListResponse,
  parseInvestorDetails,
  parseInvestorListResponse,
  parseComplianceKycListResponse,
  parseComplianceKycDetails,
  parseInvestorOnboardingChecks,
  parseInvestorKycSubmissionsResponse,
  parseInvestorAmlSubmissionsResponse,
  parseInvestorPepSubmissionsResponse,
  parseInvestorComplianceActivityResponse,
} from './investorParser'
import type { PaginatedResponse } from '@shared/types/pagination'
import type {
  ComplianceKycSubmissionRow,
  ComplianceAmlSubmissionRow,
  CompliancePepSubmissionRow,
  ComplianceActivityRow,
} from '../types/investorTypes'

import type { InvestorOnboardingChecksDto } from './investorContracts'

// ─── Endpoint paths ───────────────────────────────────────────────────────────

import {
  BANK_ACCOUNTS,
  COMPLIANCE_KYC,
  INVESTORS,
  ONBOARDING_PIPELINE,
  VASP_INVESTORS,
} from './investorsEndpointPaths'

// ─── Endpoints ────────────────────────────────────────────────────────────────

export async function getInvestorList(
  params: GetInvestorListParams,
): Promise<InvestorListResponse> {
  // API expects camelCase query params on this endpoint.
  const apiParams: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) apiParams.search = params.search
  if (params.investorType) apiParams.investorType = params.investorType
  if (params.kycStatus) apiParams.kycStatus = params.kycStatus
  if (params.bankAccountStatus) apiParams.bankAccountStatus = params.bankAccountStatus
  if (params.investorStatus) apiParams.investorStatus = params.investorStatus
  // The BE list DTO accepts `investorStatus[]` and `onboardingStatus[]` as
  // arrays. Axios serialises an array param as `?investorStatus=a&investorStatus=b`.
  if (params.investorStatuses?.length) apiParams.investorStatus = params.investorStatuses
  if (params.onboardingStatuses?.length) apiParams.onboardingStatus = params.onboardingStatuses
  // countryId / fromDate / toDate are URL-synced for shareable filter state;
  // the BE list endpoint doesn't act on them today — forwarded so the day they
  // land server-side no client change is needed.
  if (params.countryId) apiParams.countryId = params.countryId
  if (params.fromDate) apiParams.fromDate = params.fromDate
  if (params.toDate) apiParams.toDate = params.toDate

  const { data } = await api.get(INVESTORS, { params: apiParams })
  return parseInvestorListResponse(data.data || data)
}

export async function getComplianceKycList(
  params: GetInvestorListParams,
): Promise<ComplianceKycListResponse> {
  const apiParams: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) apiParams.search = params.search
  if (params.investorId) apiParams.investorId = params.investorId
  if (params.investorType) apiParams.investorType = params.investorType
  if (params.kycStatus) apiParams.kycStatus = params.kycStatus
  if (params.bankAccountStatus) apiParams.bankAccountStatus = params.bankAccountStatus
  if (params.investorStatus) apiParams.investorStatus = params.investorStatus

  const { data } = await api.get(COMPLIANCE_KYC, { params: apiParams })
  return parseComplianceKycListResponse(data.data || data)
}

export async function getComplianceKycDetails(investorId: string): Promise<ComplianceKycDetails> {
  const { data } = await api.get(`${COMPLIANCE_KYC}/${investorId}`)
  return parseComplianceKycDetails(data.data || data)
}

export async function getInvestorOnboardingChecks(
  investorId: string,
): Promise<import('../types/investorTypes').InvestorOnboardingChecks> {
  const { data } = await api.get<
    { data: InvestorOnboardingChecksDto } | InvestorOnboardingChecksDto
  >(`${INVESTORS}/${investorId}/onboarding-checks`)
  return parseInvestorOnboardingChecks(unwrap(data))
}

export async function getInvestorDetails(investorId: string): Promise<InvestorDetails> {
  const { data } = await api.get(`${INVESTORS}/${investorId}`)
  return parseInvestorDetails(data.data || data)
}

export async function updateInvestorStatus(
  investorId: string,
  input: UpdateInvestorStatusInput,
): Promise<void> {
  const payload: UpdateInvestorStatusPayloadDto = {
    investor_status: input.investorStatus,
    change_reason: input.changeReason,
  }
  await api.patch(`${INVESTORS}/${investorId}/status`, payload)
}

export async function rejectKycSubmission(
  kycId: string,
  payload: RejectKycPayloadDto,
): Promise<void> {
  await api.patch(`${COMPLIANCE_KYC}/${kycId}/reject`, payload)
}

export async function approveAmlScreening(
  amlId: string,
  input: ApproveAmlScreeningInput,
): Promise<void> {
  const payload: ApproveAmlScreeningPayloadDto = {
    sanctionsMatch: input.sanctionsMatch,
    adverseMediaMatch: input.adverseMediaMatch,
    reviewNotes: input.reviewNotes || null,
  }
  await api.patch(`/api/admin/compliance/aml/${amlId}/approve`, payload)
}

export async function approvePepScreening(
  pepId: string,
  input: ApprovePepScreeningInput,
): Promise<void> {
  const payload: ApprovePepScreeningPayloadDto = {
    isPep: input.isPep,
    reviewNotes: input.reviewNotes || null,
  }
  await api.patch(`/api/admin/compliance/pep/${pepId}/approve`, payload)
}

// AML / PEP reject — entity-agnostic; the same endpoint serves investor and
// property-owner submissions. The server stores the operator's reason in the
// submission's review_notes column.
export async function rejectAmlScreening(amlId: string, reason: string): Promise<void> {
  await api.patch(`/api/admin/compliance/aml/${amlId}/reject`, { reason })
}

export async function rejectPepScreening(pepId: string, reason: string): Promise<void> {
  await api.patch(`/api/admin/compliance/pep/${pepId}/reject`, { reason })
}

// `InvestorComplianceProfile` view-model lives in
// `../types/investorTypes` per the module layering rule (components +
// presentational consumers import from `types`, not `api`).
import type { InvestorComplianceProfile } from '../types/investorTypes'
export type { InvestorComplianceProfile }

export async function getInvestorComplianceProfile(
  investorId: string,
): Promise<InvestorComplianceProfile> {
  const { data } = await api.get(`${INVESTORS}/${investorId}/compliance-profile`)
  return (data?.data ?? data) as InvestorComplianceProfile
}

// Returns null when the investor hasn't completed the risk-profile questionnaire.
export async function getInvestorRiskProfile(
  investorId: string,
): Promise<InvestorRiskProfile | null> {
  const { data } = await api.get(`${INVESTORS}/${investorId}/risk-profile`)
  return (data?.data ?? null) as InvestorRiskProfile | null
}

// Returns null when the investor hasn't declared a source of funds.
export async function getInvestorSourceOfFunds(
  investorId: string,
): Promise<InvestorSourceOfFunds | null> {
  const { data } = await api.get(`${INVESTORS}/${investorId}/source-of-funds`)
  return (data?.data ?? null) as InvestorSourceOfFunds | null
}

export async function updateDocumentStatus(
  investorId: string,
  input: UpdateDocumentInput,
): Promise<void> {
  const payload: UpdateDocumentPayloadDto = {
    document_id: input.documentId,
    document_status: input.documentStatus,
    rejection_reason: input.rejectionReason,
  }
  await api.patch(`${INVESTORS}/${investorId}/documents/status`, payload)
}

export async function getBankAccountsList(
  params: GetBankAccountsParams,
): Promise<BankAccountListResponse> {
  const { data } = await api.get(BANK_ACCOUNTS, { params })
  return parseBankAccountListResponse(data.data || data)
}

export async function getBankAccountDetails(accountId: string): Promise<BankAccount> {
  const { data } = await api.get(`${BANK_ACCOUNTS}/${accountId}`)
  return parseBankAccount(data.data || data)
}

export async function reviewBankAccount(
  accountId: string,
  input: ReviewBankAccountInput,
): Promise<void> {
  const payload: ReviewBankAccountPayloadDto = {
    accountStatus: input.accountStatus,
    rejectionReason: input.rejectionReason,
  }
  await api.patch(`${BANK_ACCOUNTS}/${accountId}`, payload)
}

export async function deleteBankAccount(accountId: string): Promise<void> {
  await api.delete(`${BANK_ACCOUNTS}/${accountId}`)
}

export async function reInitiateKyc(investorId: string): Promise<void> {
  await api.patch(`${INVESTORS}/${investorId}/kyc/re-initiate`)
}

export async function approveInvestorOnboarding(
  investorId: string,
  _input?: ApproveInvestorOnboardingInput,
): Promise<void> {
  const payload: ApproveInvestorOnboardingPayloadDto = {}
  await api.patch(`${INVESTORS}/${investorId}/onboarding/approve`, payload)
}

export async function rejectInvestorOnboarding(
  investorId: string,
  input: RejectInvestorOnboardingInput,
): Promise<void> {
  const payload: RejectInvestorOnboardingPayloadDto = {
    rejectionReason: input.rejectionReason,
  }
  await api.patch(`${INVESTORS}/${investorId}/onboarding/reject`, payload)
}

export async function approveKycSubmission(investorId: string, formData: FormData): Promise<void> {
  await api.patch(`${COMPLIANCE_KYC}/${investorId}/approve`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export async function getInvestorStatusCounts(): Promise<InvestorStatusCountsDto> {
  const { data } = await api.get<{ data: InvestorStatusCountsDto } | InvestorStatusCountsDto>(
    `${INVESTORS}/status-counts`,
  )
  return unwrap(data)
}

export async function blockInvestor(investorId: string, reason: string): Promise<void> {
  await api.post(`${INVESTORS}/${investorId}/block`, { reason })
}

export async function unblockInvestor(investorId: string, reason?: string): Promise<void> {
  await api.post(`${INVESTORS}/${investorId}/unblock`, reason ? { reason } : {})
}

export async function unrejectInvestor(investorId: string, reason?: string): Promise<void> {
  await api.post(`${INVESTORS}/${investorId}/un-reject`, reason ? { reason } : {})
}

// ─── Per-queue status-counts ──────────────────────────────────────────────────
// Each work queue exposes its own /status-counts route for sidebar badges.
// All return camelCase JSON; tolerate `{ data: ... }` envelope defensively.

function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export interface KycPendingStatusCounts {
  pending: number
}
export async function getKycPendingStatusCounts(): Promise<KycPendingStatusCounts> {
  const { data } = await api.get<KycPendingStatusCounts | { data: KycPendingStatusCounts }>(
    `${COMPLIANCE_KYC}/status-counts`,
  )
  const result = unwrap(data)
  return { pending: Number(result.pending ?? 0) }
}

export interface KycExpiredStatusCounts {
  expired: number
}
export async function getKycExpiredStatusCounts(): Promise<KycExpiredStatusCounts> {
  const { data } = await api.get<KycExpiredStatusCounts | { data: KycExpiredStatusCounts }>(
    `${COMPLIANCE_KYC}/expired/status-counts`,
  )
  const result = unwrap(data)
  return { expired: Number(result.expired ?? 0) }
}

export interface BankVerificationStatusCounts {
  pendingVerification: number
}
export async function getBankVerificationStatusCounts(): Promise<BankVerificationStatusCounts> {
  const { data } = await api.get<
    BankVerificationStatusCounts | { data: BankVerificationStatusCounts }
  >(`${BANK_ACCOUNTS}/status-counts`)
  const result = unwrap(data)
  return { pendingVerification: Number(result.pendingVerification ?? 0) }
}

export interface VaspOnboardingStatusCounts {
  pendingVaspApproval: number
}
export async function getVaspOnboardingStatusCounts(): Promise<VaspOnboardingStatusCounts> {
  const { data } = await api.get<VaspOnboardingStatusCounts | { data: VaspOnboardingStatusCounts }>(
    `${VASP_INVESTORS}/status-counts`,
  )
  const result = unwrap(data)
  return { pendingVaspApproval: Number(result.pendingVaspApproval ?? 0) }
}

export interface RejectedInvestorsStatusCounts {
  rejected: number
}
export async function getRejectedInvestorsStatusCounts(): Promise<RejectedInvestorsStatusCounts> {
  const { data } = await api.get<
    RejectedInvestorsStatusCounts | { data: RejectedInvestorsStatusCounts }
  >(`${INVESTORS}/rejected/status-counts`)
  const result = unwrap(data)
  return { rejected: Number(result.rejected ?? 0) }
}

// Keyed by the `OnboardingStep` enum (snake_case) the pipeline cards + `step`
// filter use. The BE returns camelCase fields, so we remap here — that mismatch
// is why every multi-word card read 0 while `registered` (one word) worked.
export type OnboardingPipelineStatusCounts = Record<string, number>
interface OnboardingPipelineStatusCountsDto {
  registered?: number
  kycPending?: number
  kycRejected?: number
  kycExpired?: number
  amlPending?: number
  pepPending?: number
  pendingReview?: number
  bankPending?: number
  vaspPending?: number
}
export async function getOnboardingPipelineStatusCounts(): Promise<OnboardingPipelineStatusCounts> {
  const { data } = await api.get<
    OnboardingPipelineStatusCountsDto | { data: OnboardingPipelineStatusCountsDto }
  >(`${ONBOARDING_PIPELINE}/status-counts`)
  const result = unwrap(data)
  return {
    registered: Number(result.registered ?? 0),
    kyc_pending: Number(result.kycPending ?? 0),
    kyc_rejected: Number(result.kycRejected ?? 0),
    kyc_expired: Number(result.kycExpired ?? 0),
    aml_pending: Number(result.amlPending ?? 0),
    pep_pending: Number(result.pepPending ?? 0),
    pending_review: Number(result.pendingReview ?? 0),
    bank_pending: Number(result.bankPending ?? 0),
    vasp_pending: Number(result.vaspPending ?? 0),
  }
}

// ─── New list endpoints (KYC expired, onboarding pipeline, rejected) ──────────

export async function getKycExpiredList(
  params: GetInvestorListParams,
): Promise<ComplianceKycListResponse> {
  const apiParams: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) apiParams.search = params.search
  const { data } = await api.get(`${COMPLIANCE_KYC}/expired`, { params: apiParams })
  return parseComplianceKycListResponse(data.data || data)
}

export interface GetOnboardingPipelineParams extends GetInvestorListParams {
  step?: string
  /** Per-investor lookup. Pipeline endpoint accepts `email` as exact-match. */
  email?: string
}
export async function getOnboardingPipelineList(
  params: GetOnboardingPipelineParams,
): Promise<InvestorListResponse> {
  const apiParams: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) apiParams.search = params.search
  if (params.step) apiParams.step = params.step
  if (params.email) apiParams.email = params.email
  const { data } = await api.get(ONBOARDING_PIPELINE, { params: apiParams })
  return parseInvestorListResponse(data.data || data)
}

export async function getRejectedInvestorsList(
  params: GetInvestorListParams,
): Promise<InvestorListResponse> {
  const apiParams: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) apiParams.search = params.search
  const { data } = await api.get(`${INVESTORS}/rejected`, { params: apiParams })
  return parseInvestorListResponse(data.data || data)
}

export async function getOnboardingReviewInvestorsList(
  params: GetInvestorListParams,
): Promise<InvestorListResponse> {
  const apiParams: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) apiParams.search = params.search
  if (params.investorId) apiParams.investorId = params.investorId
  if (params.investorType) apiParams.investorType = params.investorType
  const { data } = await api.get(`${INVESTORS}/onboarding-review`, { params: apiParams })
  return parseInvestorListResponse(data.data || data)
}

// ─── Reset KYC action ─────────────────────────────────────────────────────────

export async function resetKyc(kycId: string, reason: string): Promise<void> {
  await api.post(`${COMPLIANCE_KYC}/${kycId}/reset`, { reason })
}

// ─── Compliance history feeds (per investor) ──────────────────────────────────
// Four BE endpoints emit `{ data, meta }` paginated responses. The query
// hooks consume `PaginatedResponse<T>` directly. Page-size 20 is the
// product default; callers may override.

export async function getInvestorKycSubmissions(
  investorId: string,
  page: number,
  limit: number,
): Promise<PaginatedResponse<ComplianceKycSubmissionRow>> {
  const { data } = await api.get(`${INVESTORS}/${investorId}/compliance/kyc`, {
    params: { page, limit },
  })
  return parseInvestorKycSubmissionsResponse(data.data ?? data)
}

export async function getInvestorAmlSubmissions(
  investorId: string,
  page: number,
  limit: number,
): Promise<PaginatedResponse<ComplianceAmlSubmissionRow>> {
  const { data } = await api.get(`${INVESTORS}/${investorId}/compliance/aml`, {
    params: { page, limit },
  })
  return parseInvestorAmlSubmissionsResponse(data.data ?? data)
}

export async function getInvestorPepSubmissions(
  investorId: string,
  page: number,
  limit: number,
): Promise<PaginatedResponse<CompliancePepSubmissionRow>> {
  const { data } = await api.get(`${INVESTORS}/${investorId}/compliance/pep`, {
    params: { page, limit },
  })
  return parseInvestorPepSubmissionsResponse(data.data ?? data)
}

export async function getInvestorComplianceActivity(
  investorId: string,
  page: number,
  limit: number,
): Promise<PaginatedResponse<ComplianceActivityRow>> {
  const { data } = await api.get(`${INVESTORS}/${investorId}/compliance/activity`, {
    params: { page, limit },
  })
  return parseInvestorComplianceActivityResponse(data.data ?? data)
}
